import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

// Maps each Markdown element to the app's Material Design 3 theme tokens. Base
// text color is inherited from the wrapping element in ChatWindow (so this
// works for persona text, user-bubble text, and error text); only links and
// code get their own colors. `node` is stripped from each renderer so it is
// never spread onto a DOM element.
const components: Components = {
  p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  a: ({ node, ...props }) => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:text-primary-fixed break-words"
      {...props}
    />
  ),
  ul: ({ node, className, ...props }) => {
    const isTaskList = /contains-task-list/.test(className || "");
    return (
      <ul
        className={
          isTaskList
            ? "list-none pl-1 mb-3 space-y-1 last:mb-0"
            : "list-disc pl-5 mb-3 space-y-1 last:mb-0"
        }
        {...props}
      />
    );
  },
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 mb-3 space-y-1 last:mb-0" {...props} />
  ),
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  h1: ({ node, ...props }) => (
    <h1 className="font-headline font-bold text-lg mt-4 mb-2 first:mt-0" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="font-headline font-bold text-base mt-4 mb-2 first:mt-0" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="font-headline font-semibold text-base mt-3 mb-1.5 first:mt-0" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 className="font-headline font-semibold text-sm mt-3 mb-1 first:mt-0" {...props} />
  ),
  h5: ({ node, ...props }) => (
    <h5 className="font-headline font-semibold text-sm mt-2 mb-1 first:mt-0" {...props} />
  ),
  h6: ({ node, ...props }) => (
    <h6
      className="font-headline font-semibold text-xs uppercase tracking-wide text-on-surface-variant mt-2 mb-1 first:mt-0"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="border-l-2 border-primary/40 pl-4 my-3 italic text-on-surface-variant"
      {...props}
    />
  ),
  pre: ({ node, ...props }) => (
    <pre
      className="bg-surface-container-high text-on-surface rounded-md p-3 my-3 overflow-x-auto text-sm"
      {...props}
    />
  ),
  code: ({ node, className, children, ...props }) => {
    const isBlock = /language-/.test(className || "") || /\n/.test(String(children));
    if (isBlock) {
      return (
        <code className={`font-mono text-sm ${className ?? ""}`.trim()} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="font-mono text-[0.85em] bg-surface-container-high px-1.5 py-0.5 rounded"
        {...props}
      >
        {children}
      </code>
    );
  },
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th
      className="border-b border-outline-variant text-left font-semibold px-3 py-2"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td className="border-b border-outline-variant/40 px-3 py-2 align-top" {...props} />
  ),
  hr: ({ node, ...props }) => <hr className="border-outline-variant/40 my-4" {...props} />,
  del: ({ node, ...props }) => <del className="line-through opacity-80" {...props} />,
  input: ({ node, ...props }) => (
    <input className="mr-2 accent-primary align-middle" {...props} />
  ),
};

// The chat bubble sets a comfortable reading scale. The persona pane and the
// briefing roster are narrower and denser, so headings and rhythm tighten —
// but everything else (links, code, tables, task lists) stays shared, because
// forking the whole map is how the two silently diverge.
const compactComponents: Components = {
  ...components,
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
  ul: ({ node, className, ...props }) => {
    const isTaskList = /contains-task-list/.test(className || "");
    return (
      <ul
        className={
          isTaskList
            ? "list-none pl-1 mb-2 space-y-0.5 last:mb-0"
            : "list-disc pl-4 mb-2 space-y-0.5 last:mb-0"
        }
        {...props}
      />
    );
  },
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-4 mb-2 space-y-0.5 last:mb-0" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="font-headline font-semibold text-sm mt-3 mb-1 first:mt-0" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4
      className="font-label font-bold text-[10px] uppercase tracking-[0.16em] text-on-surface-variant mt-3 mb-1.5 first:mt-0"
      {...props}
    />
  ),
};

interface MarkdownMessageProps {
  text: string;
  /** "chat" is the message-bubble scale; "compact" suits the 320px panes. */
  variant?: "chat" | "compact";
}

// `remark-breaks` turns every single newline into a <br>. That is right for
// chat, where a newline is one a person actually typed and expects to keep.
// It is wrong for authored prose: scenario briefings and persona bios are
// written as hand-wrapped Markdown, where an intra-paragraph newline is just
// where the line happened to end, and honouring it leaves a ragged right edge
// in the narrow panes. Standard Markdown reflows those; only the chat variant
// opts out.
const chatPlugins = [remarkGfm, remarkBreaks];
const compactPlugins = [remarkGfm];

// Renders a chat message body as GitHub-Flavored Markdown. Raw HTML is NOT
// rendered (no rehype-raw), so model-generated HTML is shown as inert text.
const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ text, variant = "chat" }) => {
  const isCompact = variant === "compact";
  return (
    <ReactMarkdown
      remarkPlugins={isCompact ? compactPlugins : chatPlugins}
      components={isCompact ? compactComponents : components}
    >
      {text}
    </ReactMarkdown>
  );
};

export default MarkdownMessage;
